import { Store } from '@tanstack/store'
import { useStore } from '@tanstack/react-store'

export type NotificationVariant = 'success' | 'error' | 'info' | 'destructive'

export interface Notification {
  id: string
  title: string
  description?: string
  variant: NotificationVariant
}

interface NotificationState {
  items: Array<Notification>
}

const notificationsStore = new Store<NotificationState>({
  items: [],
})

function removeNotification(id: string) {
  notificationsStore.setState((state) => ({
    ...state,
    items: state.items.filter((item) => item.id !== id),
  }))
}

function addNotification(
  notification: Omit<Notification, 'id'>,
  durationMs = 3000,
) {
  const id = crypto.randomUUID()
  const item: Notification = { id, ...notification }

  notificationsStore.setState((state) => ({
    ...state,
    items: [item, ...state.items],
  }))

  if (typeof window !== 'undefined') {
    window.setTimeout(() => removeNotification(id), durationMs)
  }
}

export const notificationActions = {
  addNotification,
  removeNotification,
  success(title: string, description?: string) {
    addNotification({ title, description, variant: 'success' })
  },
  error(title: string, description?: string) {
    addNotification({ title, description, variant: 'error' })
  },
  info(title: string, description?: string) {
    addNotification({ title, description, variant: 'info' })
  },
  destructive(title: string, description?: string) {
    addNotification({ title, description, variant: 'destructive' })
  },
}

export function useNotifications() {
  const state = useStore(notificationsStore)
  return { ...state, ...notificationActions }
}
